import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  AgreementStatus,
  GenerateInvoiceResponse,
  ServiceAgreementArchiveResponse,
  ServiceAgreementCreateInput,
  ServiceAgreementCreateResponse,
  ServiceAgreementInvoicesResponse,
  ServiceAgreementStatusResponse,
  ServiceAgreementsResponse,
  ServiceAgreementUpdateResponse,
} from './contractsMirror.js';
import type { ServiceAgreementsSearch } from './searchParams.js';

/**
 * TanStack Query hooks for the COARSE service-agreements endpoint (the strangler-fig +
 * coarse-endpoint proof, CARVE-07). One query key per `{status}` — selecting a status pill triggers
 * exactly one refetch of `GET /api/v1/service-agreements?status=…`. The SERVER computes the enriched
 * rows + the status counts + the summary (22-12); the SPA only renders the returned response (no
 * client re-aggregation, no new N+1 — threat T-22-tenant / CARVE-07).
 *
 * THE GUARDRAIL (Pitfall 3 / D-03): the list GET is READ-ONLY. Auto-invoicing runs off-page in
 * Cron+Queue — rendering the list never generates an invoice. Only the explicit generate-invoice
 * mutation creates one.
 *
 * Every request goes through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is derived server-side from
 * the auth context; the SPA NEVER sends a tenant id param (threat T-22-tenant).
 */

/** Build the stable query key for a filter (one cache entry per status). */
export function agreementsQueryKey(search: ServiceAgreementsSearch): readonly unknown[] {
  return ['service-agreements', search.status ?? null] as const;
}

/** Build the coarse-endpoint request path with the active filter (no tenant param, read-only). */
export function agreementsRequestPath(search: ServiceAgreementsSearch): string {
  const params = new URLSearchParams();
  if (search.status) params.set('status', search.status);
  const qs = params.toString();
  return qs ? `/api/v1/service-agreements?${qs}` : '/api/v1/service-agreements';
}

export function useAgreementsQuery(search: ServiceAgreementsSearch) {
  return useQuery<ServiceAgreementsResponse>({
    queryKey: agreementsQueryKey(search),
    queryFn: () => apiFetchJson<ServiceAgreementsResponse>(agreementsRequestPath(search)),
  });
}

/** The agreement-create mutation. NO tenantId — the server resolves the tenant from the principal. */
export function useCreateAgreementMutation() {
  const queryClient = useQueryClient();
  return useMutation<ServiceAgreementCreateResponse, Error, ServiceAgreementCreateInput>({
    mutationFn: (input) =>
      apiFetchJson<ServiceAgreementCreateResponse>('/api/v1/service-agreements', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-agreements'] });
    },
  });
}

/**
 * The agreement-edit mutation. PATCHes `/api/v1/service-agreements/:id` with the SAME
 * `ServiceAgreementCreateInput` payload shape (the server reuses the create validation block) and
 * invalidates the list so the row re-syncs. NO tenantId — the server resolves the tenant from the
 * principal (SEC-11).
 */
export function useEditAgreementMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    ServiceAgreementUpdateResponse,
    Error,
    { agreementId: number; input: ServiceAgreementCreateInput }
  >({
    mutationFn: ({ agreementId, input }) =>
      apiFetchJson<ServiceAgreementUpdateResponse>(`/api/v1/service-agreements/${agreementId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-agreements'] });
    },
  });
}

/**
 * The agreement-archive mutation. POSTs `/api/v1/service-agreements/:id/archive` (soft archive) and
 * invalidates the list so the archived row disappears. The server re-checks invoices.edit + scopes
 * the archive to the principal tenant (SEC-11). NO tenantId in the body.
 */
export function useArchiveAgreementMutation() {
  const queryClient = useQueryClient();
  return useMutation<ServiceAgreementArchiveResponse, Error, { agreementId: number }>({
    mutationFn: ({ agreementId }) =>
      apiFetchJson<ServiceAgreementArchiveResponse>(
        `/api/v1/service-agreements/${agreementId}/archive`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-agreements'] });
    },
  });
}

/**
 * The generated-invoices read for a single agreement. GETs
 * `/api/v1/service-agreements/:id/invoices` (a PURE READ) — the server scopes the list to the
 * principal tenant and only returns invoices this agreement generated. One cache entry per agreement.
 */
export function useAgreementInvoicesQuery(agreementId: number) {
  return useQuery<ServiceAgreementInvoicesResponse>({
    queryKey: ['service-agreements', agreementId, 'invoices'],
    queryFn: () =>
      apiFetchJson<ServiceAgreementInvoicesResponse>(
        `/api/v1/service-agreements/${agreementId}/invoices`,
      ),
    enabled: Number.isFinite(agreementId) && agreementId > 0,
  });
}

/**
 * The status mutation (pause/cancel/resume). PATCHes `/api/v1/service-agreements/:id/status` and
 * invalidates the list so the row re-syncs. The server re-checks the invoices.edit permission +
 * scopes the UPDATE to the principal tenant (SEC-11). NO tenantId in the body.
 */
export function useAgreementStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    ServiceAgreementStatusResponse,
    Error,
    { agreementId: number; status: AgreementStatus }
  >({
    mutationFn: ({ agreementId, status }) =>
      apiFetchJson<ServiceAgreementStatusResponse>(
        `/api/v1/service-agreements/${agreementId}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status }),
          headers: { 'Content-Type': 'application/json' },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-agreements'] });
    },
  });
}

/**
 * The SINGLE explicit generate-invoice mutation — the ONLY invoice-creating action (D-03). POSTs to
 * `/api/v1/service-agreements/:id/generate-invoice`; the server claims the ledger on the agreement's
 * billing period so a double-submit creates NO second invoice. NO tenantId — the server resolves the
 * tenant from the principal (SEC-11).
 */
export function useGenerateInvoiceMutation() {
  const queryClient = useQueryClient();
  return useMutation<GenerateInvoiceResponse, Error, { agreementId: number }>({
    mutationFn: ({ agreementId }) =>
      apiFetchJson<GenerateInvoiceResponse>(
        `/api/v1/service-agreements/${agreementId}/generate-invoice`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-agreements'] });
    },
  });
}
